import { AUTHZED_RELATIONS } from '@/modules/authentication/rolesConstants';
import { AuthorizationLevel } from '@/types/schema';

import { AdminRolesPage_RoleFragment } from '../graphql/GetRoles.generated';
import { RoleFormData, WaterfallAccess } from './RoleViewer.types';

function generateRandomString(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function generateSlugFromName(name: string): string {
  const baseSlug = name
    .toLowerCase()
    .replace(/[^a-z-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const entropy = generateRandomString(6);
  const slug = baseSlug ? `${baseSlug}-${entropy}` : entropy;

  return slug;
}

function getAuthorizationLevel(
  authzedRelations: string[],
  relationGroup: { viewer?: string; editor?: string; admin?: string },
  defaultLevel: AuthorizationLevel = AuthorizationLevel.None
): AuthorizationLevel {
  if (relationGroup.admin && authzedRelations.includes(relationGroup.admin)) {
    return AuthorizationLevel.Admin;
  }
  if (relationGroup.editor && authzedRelations.includes(relationGroup.editor)) {
    return AuthorizationLevel.Editor;
  }
  if (relationGroup.viewer && authzedRelations.includes(relationGroup.viewer)) {
    return AuthorizationLevel.View;
  }
  return defaultLevel;
}

// Helper function to convert form data to required authzed relations
function getRequiredRelationsFromFormData(formData: RoleFormData): string[] {
  const requiredRelations: string[] = [];

  // Add general properties capabilities
  if (formData.generalProperties.canActAsRelationshipOwner) {
    requiredRelations.push(AUTHZED_RELATIONS.advisorClientRelationshipOwner);
  }
  if (formData.generalProperties.hasAllHouseholdsAccess) {
    requiredRelations.push(AUTHZED_RELATIONS.advisorClientAllClients);
  }
  if (formData.generalProperties.canModifyGlobalCategories) {
    requiredRelations.push(AUTHZED_RELATIONS.globalCategoriesEditor);
  }
  if (formData.generalProperties.canModifyPrompts) {
    requiredRelations.push(AUTHZED_RELATIONS.promptEditor);
  }
  if (formData.generalProperties.canAccessTenantReporting) {
    requiredRelations.push(AUTHZED_RELATIONS.tenantReportingViewer);
  }
  if (formData.generalProperties.canApproveLuminarySupportTeamAccess) {
    requiredRelations.push(AUTHZED_RELATIONS.approveSupportAccess);
  }
  if (formData.generalProperties.canApproveClientPresentations) {
    requiredRelations.push(AUTHZED_RELATIONS.presentationApprover);
  }

  // Add authorization level capabilities
  if (formData.waterfall === WaterfallAccess.Editor) {
    requiredRelations.push(AUTHZED_RELATIONS.waterfall.editor);
  } else if (formData.waterfall === WaterfallAccess.Presentation) {
    requiredRelations.push(AUTHZED_RELATIONS.waterfall.presenter);
  }

  const authLevelMappings = [
    { level: formData.branding, relations: AUTHZED_RELATIONS.branding },
    { level: formData.employees, relations: AUTHZED_RELATIONS.employees },
    {
      level: formData.collaborators,
      relations: AUTHZED_RELATIONS.collaborators,
    },
    { level: formData.households, relations: AUTHZED_RELATIONS.households },
    {
      level: formData.dispositiveProvisions,
      relations: AUTHZED_RELATIONS.dispositiveProvisions,
    },
    { level: formData.auditLogs, relations: AUTHZED_RELATIONS.auditLogs },
    { level: formData.roles, relations: AUTHZED_RELATIONS.roles },
    {
      level: formData.presentationTemplates,
      relations: AUTHZED_RELATIONS.presentationTemplates,
    },
  ];

  authLevelMappings.forEach(({ level, relations }) => {
    switch (level) {
      case AuthorizationLevel.Admin:
        if ('admin' in relations && relations.admin) {
          requiredRelations.push(relations.admin);
        }
        break;
      case AuthorizationLevel.Editor:
        if ('editor' in relations && relations.editor) {
          requiredRelations.push(relations.editor);
        }
        break;
      case AuthorizationLevel.View:
        if ('viewer' in relations && relations.viewer) {
          requiredRelations.push(relations.viewer);
        }
        break;
    }
  });

  return requiredRelations;
}

// Helper function to extract form values from role capabilities
export function getRoleFormValues(
  role: AdminRolesPage_RoleFragment | undefined
): RoleFormData {
  if (!role) {
    return {
      name: '',
      slug: '',
      isDefault: false,
      generalProperties: {
        canActAsRelationshipOwner: false,
        hasAllHouseholdsAccess: false,
        canModifyGlobalCategories: false,
        canModifyPrompts: false,
        canAccessTenantReporting: false,
        canApproveLuminarySupportTeamAccess: false,
        canApproveClientPresentations: false,
      },
      branding: AuthorizationLevel.None,
      employees: AuthorizationLevel.View,
      collaborators: AuthorizationLevel.None,
      households: AuthorizationLevel.None,
      waterfall: WaterfallAccess.Editor,
      dispositiveProvisions: AuthorizationLevel.Editor,
      auditLogs: AuthorizationLevel.None,
      roles: AuthorizationLevel.None,
      presentationTemplates: AuthorizationLevel.None,
    };
  }

  const capabilities = role.capabilities || [];
  const authzedRelations = capabilities.map((cap) => cap.authzedRelationName);

  let waterfall = WaterfallAccess.View;
  if (authzedRelations.includes(AUTHZED_RELATIONS.waterfall.editor)) {
    waterfall = WaterfallAccess.Editor;
  } else if (authzedRelations.includes(AUTHZED_RELATIONS.waterfall.presenter)) {
    waterfall = WaterfallAccess.Presentation;
  }

  return {
    name: role.displayName,
    slug: role.slug ?? '',
    isDefault: role.isDefault,
    generalProperties: {
      canActAsRelationshipOwner: authzedRelations.includes(
        AUTHZED_RELATIONS.advisorClientRelationshipOwner
      ),
      hasAllHouseholdsAccess: authzedRelations.includes(
        AUTHZED_RELATIONS.advisorClientAllClients
      ),
      canModifyGlobalCategories: authzedRelations.includes(
        AUTHZED_RELATIONS.globalCategoriesEditor
      ),
      canModifyPrompts: authzedRelations.includes(
        AUTHZED_RELATIONS.promptEditor
      ),
      canAccessTenantReporting: authzedRelations.includes(
        AUTHZED_RELATIONS.tenantReportingViewer
      ),
      canApproveLuminarySupportTeamAccess: authzedRelations.includes(
        AUTHZED_RELATIONS.approveSupportAccess
      ),
      canApproveClientPresentations: authzedRelations.includes(
        AUTHZED_RELATIONS.presentationApprover
      ),
    },
    branding:
      getAuthorizationLevel(authzedRelations, AUTHZED_RELATIONS.branding) ??
      AuthorizationLevel.None,
    employees: getAuthorizationLevel(
      authzedRelations,
      AUTHZED_RELATIONS.employees,
      AuthorizationLevel.View
    ),
    collaborators:
      getAuthorizationLevel(
        authzedRelations,
        AUTHZED_RELATIONS.collaborators
      ) ?? AuthorizationLevel.None,
    households:
      getAuthorizationLevel(authzedRelations, AUTHZED_RELATIONS.households) ??
      AuthorizationLevel.None,
    auditLogs:
      getAuthorizationLevel(authzedRelations, AUTHZED_RELATIONS.auditLogs) ??
      AuthorizationLevel.None,
    waterfall,
    dispositiveProvisions: authzedRelations.includes(
      AUTHZED_RELATIONS.dispositiveProvisions.editor
    )
      ? AuthorizationLevel.Editor
      : AuthorizationLevel.View,
    roles:
      getAuthorizationLevel(authzedRelations, AUTHZED_RELATIONS.roles) ??
      AuthorizationLevel.None,
    presentationTemplates:
      getAuthorizationLevel(
        authzedRelations,
        AUTHZED_RELATIONS.presentationTemplates
      ) ?? AuthorizationLevel.None,
  };
}

// Helper function to map form data to update input
// Note: This requires all available capabilities to properly map form data to capability IDs
export function getUpdateRoleInput(
  roleId: string,
  formData: RoleFormData,
  existingRole: AdminRolesPage_RoleFragment,
  allCapabilities: Array<{ id: string; authzedRelationName: string }>
) {
  const existingCapabilities = existingRole.capabilities || [];

  // Get the capabilities that should be present based on form data
  const requiredRelations = getRequiredRelationsFromFormData(formData);

  // Find capabilities to add and remove
  const requiredCapabilityIds = allCapabilities
    .filter((cap) => requiredRelations.includes(cap.authzedRelationName))
    .map((cap) => cap.id);

  const currentCapabilityIds = existingCapabilities.map((cap) => cap.id);

  const toAdd = requiredCapabilityIds.filter(
    (id) => !currentCapabilityIds.includes(id)
  );
  const toRemove = currentCapabilityIds.filter((id) => {
    const capability = allCapabilities.find((cap) => cap.id === id);
    return (
      capability && !requiredRelations.includes(capability.authzedRelationName)
    );
  });

  return {
    id: roleId,
    update: {
      displayName: formData.name,
      // slug is intentionally omitted: it is immutable on the backend
      // (UpdateRoleInput has no slug field) and is disabled in the edit UI.
      isDefault: formData.isDefault,
      addCapabilityIDs: toAdd.length > 0 ? toAdd : undefined,
      removeCapabilityIDs: toRemove.length > 0 ? toRemove : undefined,
    },
  };
}

// Helper function to create a role from form data
export function getCreateRoleInput(
  formData: RoleFormData,
  allCapabilities: Array<{ id: string; authzedRelationName: string }>
) {
  // Get the capabilities that should be present based on form data
  const requiredRelations = getRequiredRelationsFromFormData(formData);

  // Find capability IDs for the required relations
  const capabilityIDs = allCapabilities
    .filter((cap) => requiredRelations.includes(cap.authzedRelationName))
    .map((cap) => cap.id);

  return {
    create: {
      displayName: formData.name,
      slug: formData.slug,
      isDefault: formData.isDefault,
      capabilityIDs: capabilityIDs.length > 0 ? capabilityIDs : undefined,
    },
  };
}
